/**
 * JsBridge对象，用于和native通信
 */

import * as hbridge from '@hb/mbridge';
import { parseQueryStr } from '../utils/url';
import { uiMgr } from '../uiMgr';
import {
  isAliMP,
  isHelloFamilyApp,
  isWechatMini,
} from '../env/env';
import logger from '../utils/logger';

const { Native, env } = hbridge;

/** 定位信息结构，用于 getCityInfo 缓存 */
interface LocationInfo {
  latitude?: string;
  longitude?: string;
  cityCode?: string;
  adCode?: string;
  cityName?: string;
  city?: string;
}

class NativeMgr {
  private cityInfoPromise: Promise<any> | null = null;

  async getUserInfo(): ReturnType<typeof Native.getTokenAndKey> {
    let reqNum = 0;
    return new Promise((resolve, reject) => {
      async function getToken() {
        try {
          const res = await Native.getTokenAndKey({ needLogin: true });
          if (res) {
            resolve(res);
          }
        } catch (error) {
          reqNum += 1;
          if (reqNum > 5) {
            reject(error);
          } else {
            getToken();
          }
        }
      }

      getToken();
    });
  }

  showProgressView(isShow = true) {
    if (isHelloFamilyApp) {
      return Native.callNative({
        classMap: 'NavBar',
        method: 'showProgressView',
        params: {
          isShow,
        },
      });
    }
    return true;
  }

  isOpenLocationPermission() {
    return Native.callNative({
      classMap: 'SystemUtil',
      method: 'isOpenLocationPermission',
      params: {},
    });
  }

  showLocationAlert() {
    if (isHelloFamilyApp) {
      Native.callNative({
        classMap: 'SystemUtil',
        method: 'showLocationAlert',
        params: {},
      });
    }
  }

  hideNaviBar(isShow: boolean) {
    if (isHelloFamilyApp) {
      Native.callNative({
        classMap: 'NavBar',
        method: 'showNaviBar',
        params: { isShow },
      });
    }
  }

  setStatusBar() {
    if (isHelloFamilyApp) {
      Native.callNative({
        classMap: 'NavBar',
        method: 'setStatusBar',
        params: { isShowStatusBar: true },
      });
      Native.callNative({
        classMap: 'NavBar',
        method: 'setStatusBarColor',
        params: {
          isShowStatusBar: true,
          isDarkFont: false,
          colorCode: '#FF7532',
        },
      });
    }
  }

  closeWebView() {
    if (isAliMP) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.my.navigateBack(1 - window.history.length);
    } else if (isHelloFamilyApp) {
      Native.closeWebView();
    } else {
      // 支付成功后路由跳转至优惠券列表
      uiMgr.replacePage('Bounty');
    }
  }

  async getCityInfo() {
    logger('[getCityInfo] 开始执行');

    // 1. 优先从 URL 参数获取
    const {
      lat,
      lng,
      cityCode,
      adCode,
      cityName,
      city,
    } = parseQueryStr(true as const);

    if (lat && lng && cityCode && adCode) {
      logger('[getCityInfo] 从 URL 参数获取定位信息');
      return {
        latitude: lat,
        longitude: lng,
        cityCode,
        adCode,
        cityName,
        city,
      };
    }

    // 2. 从 window.$$location 获取
    // logger('window.$$location:', window.$$location);
    // const winLocation = window.$$location ?? {};
    // const {
    //   latitude: winLat,
    //   longitude: winLng,
    //   cityCode: winCityCode,
    //   adCode: winAdCode,
    //   cityName: winCityName,
    //   city: winCity,
    // } = winLocation;
    // if (winLat && winLng && winCityCode && winAdCode) {
    //   logger('[getCityInfo] 从 window.$$location 获取定位信息');
    //   return {
    //     latitude: winLat,
    //     longitude: winLng,
    //     cityCode: winCityCode,
    //     adCode: winAdCode,
    //     cityName: winCityName,
    //     city: winCity,
    //   };
    // }

    // 3. 如果正在请求中，返回同一个 Promise（单例锁）
    if (this.cityInfoPromise) {
      logger('[getCityInfo] 等待正在进行的定位请求');
      return this.cityInfoPromise;
    }

    // 4. 发起新的 Native 请求，请求完成直接清除 cityInfoPromise
    logger('[getCityInfo] 调用 Native 获取定位信息');
    try {
      const result = await this._fetchCityInfo();
      return result;
    } finally {
      // 请求完成后清除锁，允许下次重新获取
      this.cityInfoPromise = null;
      logger('[getCityInfo] 定位请求完成，已释放锁');
    }
  }

  private async _fetchCityInfo() {
    let cityInfo = null;

    try {
      cityInfo = await Native.getCityInfo();

      if (
        cityInfo?.adCode
        && Array.isArray(cityInfo.adCode)
      ) {
        cityInfo.adCode = '';
      }
      logger('[getCityInfo] Native 调用成功', cityInfo);
    } catch (err) {
      logger('[getCityInfo] Native 调用失败', err);
    }

    return cityInfo;
  }

  async getDeviceInfo() {
    const defVersion = '6.92.0';
    // 指定兜底版本
    let version = defVersion;
    try {
      if (isAliMP) {
        try {
          const systemInfo = await Native.callNative({
            method: 'getSystemInfo',
            params: {},
          });
          version = systemInfo?.alissVersion || version;
        } catch (error) {
          // 使用兜底版本
        }
      } else if (isHelloFamilyApp) {
        version = env.appVersion || version;
      } else if (isWechatMini) {
        const query = parseQueryStr();
        version = query.wxMiniVersion as string || version;
      }
    } catch (error) {
      // 使用兜底版本
    }

    // version 可能是数组或类似 "[6.98.90, 6.98.90]" 的字符串
    if (Array.isArray(version)) {
      version = version[0] || defVersion;
    } else if (typeof version === 'string' && version.includes('[')) {
      const match = version.match(/[\d.]+\d+/);
      version = match ? match[0] : defVersion;
    }
    // 最终兜底
    if (!version) {
      version = defVersion;
    }
    return {
      version,
    };
  }
}

export type CityInfo = Awaited<ReturnType<NativeMgr['getCityInfo']>>;

const nativeMgr = new NativeMgr();
export default nativeMgr;
